import React, { useMemo } from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { Badge } from '../components/ui/Badge';
import { Section, ErrorPanel, LoadingRow, EmptyNote, UserLink, TableWrap, Th, Td } from '../components/ui/bits';
import { useRpc, useSel } from '../lib/queries';
import { supabase } from '../lib/supabase';
import { fmtDate } from '../lib/format';
import { PACK_LIVE_UK, PACK_LIVE_US, dbSlugFor, PACK_CONSTANTS_SNAPSHOT } from '../lib/packConstants';

interface CoverageRow { slug: string; name: string; markets: string[] | null; has_content: boolean; has_us_content: boolean; content_updated_at: string | null }

export const PacksPage: React.FC = () => {
  const { data: coverage, error, isLoading } = useRpc<CoverageRow[]>('admin_pack_coverage');
  const { data: passes } = useSel<any[]>(['passes-all'], () =>
    supabase.from('firm_passes').select('*, firms(name)').order('granted_at', { ascending: false }));

  const bySlug = useMemo(() => new Map((coverage ?? []).map(r => [r.slug, r])), [coverage]);

  const matrix = useMemo(() => {
    const marketingSlugs = new Set([...PACK_LIVE_UK, ...PACK_LIVE_US]);
    return [...marketingSlugs].sort().map(mk => {
      const db = dbSlugFor(mk);
      const row = bySlug.get(db);
      return {
        marketing: mk,
        db,
        translated: mk !== db,
        liveUk: PACK_LIVE_UK.has(mk),
        liveUs: PACK_LIVE_US.has(mk),
        inDb: !!row,
        hasContent: row?.has_content ?? false,
        hasUs: row?.has_us_content ?? false,
        updated: row?.content_updated_at ?? null,
      };
    });
  }, [bySlug]);

  if (isLoading) return <LoadingRow />;
  if (error) return <ErrorPanel error={error} label="admin_pack_coverage" />;

  const gaps = matrix.filter(m => !m.inDb || !m.hasContent || (m.liveUs && !m.hasUs));

  return (
    <div>
      <PageHeader title="Packs" subtitle={`Coverage across the marketing gate, the slug map and the content table. Gate constants snapshot ${PACK_CONSTANTS_SNAPSHOT}; if the marketing repo changes, update lib/packConstants.ts.`} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatTile label="Live on marketing, UK" value={PACK_LIVE_UK.size} accent="indigo" />
        <StatTile label="Live on marketing, US" value={PACK_LIVE_US.size} accent="indigo" />
        <StatTile label="Content rows" value={(coverage ?? []).length} sub="firms flagged has_pack" accent="stone" />
        <StatTile label="Coverage gaps" value={gaps.length} accent={gaps.length ? 'rose' : 'emerald'} />
      </div>

      <Section
        title="Coverage matrix"
        sub="A red cell is a silent failure: sold but never granted (slug missing), or granted but empty (content missing), or US buyers seeing UK copy (US overlay missing)."
        className="mb-3"
      >
        <TableWrap>
          <thead>
            <tr><Th>Marketing slug</Th><Th>DB slug</Th><Th>Live UK</Th><Th>Live US</Th><Th>Firm in DB</Th><Th>Content</Th><Th>US overlay</Th><Th>Updated</Th></tr>
          </thead>
          <tbody>
            {matrix.map(m => (
              <tr key={m.marketing}>
                <Td className="font-medium text-stone-900">{m.marketing}</Td>
                <Td>{m.db}{m.translated && <Badge tone="amber" className="ml-1">translated</Badge>}</Td>
                <Td>{m.liveUk ? 'yes' : ''}</Td>
                <Td>{m.liveUs ? 'yes' : ''}</Td>
                <Td>{m.inDb ? 'yes' : <Badge tone="rose">Missing</Badge>}</Td>
                <Td>{m.hasContent ? 'yes' : <Badge tone="rose">Missing</Badge>}</Td>
                <Td>{m.liveUs ? (m.hasUs ? 'yes' : <Badge tone="rose">Missing</Badge>) : ''}</Td>
                <Td>{fmtDate(m.updated)}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
        <p className="text-[11px] text-stone-400 mt-2">
          Known related gap: the alert-email pack upsell tests firms.slug against the marketing gate without translating, so it never renders for the translated slugs above. Product fix.
        </p>
      </Section>

      <Section title="Pass ledger" sub="Every pass with its per-pass lifetime usage. Extend expiry from the holder’s user page.">
        {(passes ?? []).length === 0 ? <EmptyNote>No passes</EmptyNote> : (
          <TableWrap>
            <thead><tr><Th>User</Th><Th>Firm</Th><Th>Tier</Th><Th>Source</Th><Th>Granted</Th><Th>Expires</Th><Th>Usage</Th></tr></thead>
            <tbody>
              {(passes ?? []).map(p => (
                <tr key={p.id}>
                  <Td><UserLink id={p.user_id} /></Td>
                  <Td className="font-medium text-stone-900">{p.firms?.name ?? 'unknown'}</Td>
                  <Td>{p.tier}{p.market === 'us' ? ' (US)' : ''}</Td>
                  <Td>{p.refunded_at ? <Badge tone="rose">Refunded</Badge> : p.stripe_payment_intent ? <Badge tone="emerald">Paid</Badge> : p.stripe_session_id ? <Badge tone="amber">Session, no payment</Badge> : <Badge tone="indigo">Comped</Badge>}</Td>
                  <Td>{fmtDate(p.granted_at)}</Td>
                  <Td>{fmtDate(p.expires_at)}</Td>
                  <Td className="text-[11px] text-stone-500">
                    hv {p.usage_hv_used ?? 0} · psy {p.usage_psy_used ?? 0} · mocks {p.usage_mocks_used ?? 0} · cv {p.usage_cv_used ?? 0} · vyo {p.usage_vyo_used ?? 0}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Section>
    </div>
  );
};
